import React, { useContext, useEffect, useMemo, useState } from "react";
import SectionCard from "../components/SectionCard";
import { Label, Input, Select } from "../components/FormControls";
import { Gift } from "lucide-react";
import { toast } from "react-toastify";
import { GlobalContext } from "../../../../Context/GlobalContext";

import useOrderSettings from "../../../Services/OrderServices/OrderSettingsServices/useOrderSettings";

export default function FreeItemsSection({ userID }) {
  const {getMerchantMenuItems, addFreeItems}=useOrderSettings();

  const { api } = useContext(GlobalContext);
  const [menuCategories, setMenuCategories] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ minAmount: "", singleUse: "Y" });
  const [selectedItem, setSelectedItem] = useState(null);

const load = async () => {
  if (!userID) return;

  try {
    const result =
      await getMerchantMenuItems(userID);

    if (result.success) {
      setMenuCategories(result.data);
    } else {
      setMenuCategories([]);
      // optional:
      // toast.error(result.message);
    }
  } catch {
    setMenuCategories([]);
  }
};


  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (menuCategories || [])
      .map((cat) => ({
        ...cat,
        menu_items: (cat.menu_items || []).filter((it) => (it.item_name || "").toLowerCase().includes(q)),
      }))
      .filter((c) => (c.menu_items || []).length > 0);
  }, [menuCategories, search]);

  const toggle = (id) => setExpanded((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

 const save = async () => {
  if (
    !form.minAmount ||
    !form.singleUse ||
    !selectedItem
  )
    return toast.error(
      "Fill min amount, customer type and select item"
    );

  try {
    const result = await addFreeItems({
      userid: userID,
      minAmount: form.minAmount,
      singleUse: form.singleUse,
      item: selectedItem,
    });

    if (result.success) {
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
  } catch {
    toast.error("Failed");
  }
};


  useEffect(() => { load(); }, [userID]); // eslint-disable-line

  return (
    <SectionCard icon={Gift} title="Free Items">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* form */}
        <div className="lg:col-span-2">
          <div className="space-y-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
            <div><Label>Minimum Order Amount</Label><Input value={form.minAmount} onChange={(e)=>setForm(s=>({...s,minAmount:e.target.value}))} /></div>
            <div>
              <Label>Customer Type</Label>
              <Select value={form.singleUse} onChange={(e)=>setForm(s=>({...s,singleUse:e.target.value}))}>
                <option value="Y">First Time User Only</option>
                <option value="N">For All</option>
              </Select>
            </div>

            <div>
              <Label>Selected Free Item</Label>
              {selectedItem?.item_name ? (
                <div className="flex items-center gap-3 rounded-lg bg-gray-50 p-3">
                  <img src={selectedItem.photo} className="h-16 w-16 rounded-lg object-cover" alt="" />
                  <div>
                    <div className="text-sm font-semibold">{selectedItem.item_name}</div>
                    <div className="text-xs text-gray-500">{selectedItem?.prices?.formatted_price || ""}</div>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed p-3 text-sm text-gray-500">No item selected</div>
              )}
            </div>

            <button onClick={save} className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
              Save
            </button>
          </div>
        </div>

        {/* list */}
        <div className="lg:col-span-3">
          <div className="mb-3"><Input placeholder="Search items…" value={search} onChange={(e)=>setSearch(e.target.value)} /></div>
          <div className="max-h-[520px] space-y-4 overflow-y-auto pr-1">
            {filtered.map((cat) => {
              const id = String(cat.cat_id);
              const open = expanded.includes(id);
              return (
                <div key={id} className="rounded-xl border border-gray-100 bg-white shadow-sm">
                  <button onClick={() => toggle(id)} className="flex w-full items-center justify-between px-4 py-3 text-left">
                    <div className="text-sm font-semibold text-gray-900">{cat.category_name}</div>
                    <span className="text-xs text-gray-500">{open ? "Hide" : "Show"}</span>
                  </button>
                  {open && (
                    <div className="grid grid-cols-1 gap-3 border-t p-3 sm:grid-cols-2">
                      {(cat.menu_items || []).map((item) => (
                        <label key={item.item_id} className={
                          "flex cursor-pointer items-center gap-3 rounded-lg border p-2 " +
                          (selectedItem?.item_id === item.item_id ? "border-emerald-300 bg-emerald-50" : "border-gray-100 bg-white")
                        }>
                          <input type="radio" name="free_item" className="h-4 w-4 accent-emerald-600"
                                 checked={selectedItem?.item_id === item.item_id} onChange={()=>setSelectedItem(item)} />
                          <img src={item.photo} className="h-12 w-12 rounded object-cover" alt="" />
                          <div className="text-sm">
                            <div className="font-medium">{item.item_name}</div>
                            <div className="text-xs text-gray-500">{item?.prices?.formatted_price || ""}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
